'use client';

import {config} from '@config';
import {useBEApiClient} from '@hooks/network/useApiClient';
import type {PropsWithChildren} from 'react';
import {useEffect, useRef} from 'react';
import useSWRImmutable from 'swr/immutable';

import {ApplicationMetadataContext, createApplicationMetadataStore} from './store';

export const ApplicationMetadataProvider = (props: PropsWithChildren) => {
  const {children} = props;
  const api = useBEApiClient();

  const {data, isLoading, error} = useSWRImmutable(
    `application-logos`,
    () => api?.publicListApplicationLogos({limit: 500}),
    {
      errorRetryCount: 3,
      keepPreviousData: true,
      revalidateOnReconnect: false,
    },
  );

  const logosResponse = data?.data;
  const hasSetLogos = useRef(false);

  const storeRef = useRef<ReturnType<typeof createApplicationMetadataStore> | null>(createApplicationMetadataStore());

  useEffect(() => {
    if (error) {
      console.error('ApplicationMetadataProvider: Error fetching logos', error);
    }
  }, [error]);

  useEffect(() => {
    if (logosResponse?.items && !isLoading && !hasSetLogos.current) {
      // Transform the logo URLs to include the CDN base if they're relative paths
      const baseUrl = config.cdn.base;
      const transformedLogos = logosResponse.items.map(logo => {
        let url = logo.url;
        // If URL doesn't start with http/https, prefix with CDN base
        if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
          url = `${baseUrl}/${url}`;
        }
        return {
          application_id: logo.application_id,
          url,
        };
      });

      hasSetLogos.current = true;
      storeRef.current?.getState().setCustomLogos(transformedLogos);
    }
  }, [logosResponse, isLoading]);

  useEffect(() => {
    storeRef.current?.getState().setIsLoading(isLoading);
  }, [isLoading]);

  return <ApplicationMetadataContext.Provider value={storeRef.current}>{children}</ApplicationMetadataContext.Provider>;
};
